import React from "react";
import { Linkedin, Twitter, Facebook, Mail, Phone, MapPin } from "lucide-react";
import { companyInfo } from "../mock";

const Footer = () => {
  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-slate-950 border-t border-slate-800 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(6,182,212,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(6,182,212,0.02)_1px,transparent_1px)] bg-[size:80px_80px]"></div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Main Footer Content */}
        <div className="py-12 grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <img src={companyInfo.logo} alt={companyInfo.name} className="h-10 mb-4" />
            <p className="text-slate-400 mb-4 leading-relaxed">
              {companyInfo.description}
            </p>
            <div className="flex space-x-3">
              <a
                href={companyInfo.social.linkedin}
                className="w-10 h-10 bg-slate-800 hover:bg-cyan-500 border border-slate-700 hover:border-cyan-500 rounded-lg flex items-center justify-center transition-all duration-300 group"
              >
                <Linkedin className="w-5 h-5 text-slate-400 group-hover:text-white transition-colors duration-300" />
              </a>
              <a
                href={companyInfo.social.twitter}
                className="w-10 h-10 bg-slate-800 hover:bg-cyan-500 border border-slate-700 hover:border-cyan-500 rounded-lg flex items-center justify-center transition-all duration-300 group"
              >
                <Twitter className="w-5 h-5 text-slate-400 group-hover:text-white transition-colors duration-300" />
              </a>
              <a
                href={companyInfo.social.facebook}
                className="w-10 h-10 bg-slate-800 hover:bg-cyan-500 border border-slate-700 hover:border-cyan-500 rounded-lg flex items-center justify-center transition-all duration-300 group"
              >
                <Facebook className="w-5 h-5 text-slate-400 group-hover:text-white transition-colors duration-300" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-bold mb-4">Quick Links</h3>
            <ul className="space-y-2">
              {[
                { name: "Home", href: "#home" },
                { name: "About Us", href: "#about" },
                { name: "Services", href: "#services" },
                { name: "Projects", href: "#projects" },
                { name: "Team", href: "#team" },
                { name: "Contact", href: "#contact" }
              ].map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    onClick={(e) => scrollToSection(e, link.href)}
                    className="text-slate-400 hover:text-cyan-400 transition-colors duration-300"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-white font-bold mb-4">Our Services</h3>
            <ul className="space-y-2">
              <li>
                <a href="#services" onClick={(e) => scrollToSection(e, "#services")} className="text-slate-400 hover:text-cyan-400 transition-colors duration-300">
                  Financial AI Solutions
                </a>
              </li>
              <li>
                <a href="#services" onClick={(e) => scrollToSection(e, "#services")} className="text-slate-400 hover:text-cyan-400 transition-colors duration-300">
                  Healthcare AI
                </a>
              </li>
              <li>
                <a href="#services" onClick={(e) => scrollToSection(e, "#services")} className="text-slate-400 hover:text-cyan-400 transition-colors duration-300">
                  Education Technology
                </a>
              </li>
              <li>
                <a href="#services" onClick={(e) => scrollToSection(e, "#services")} className="text-slate-400 hover:text-cyan-400 transition-colors duration-300">
                  Custom AI Development
                </a>
              </li>
              <li>
                <a href="#services" onClick={(e) => scrollToSection(e, "#services")} className="text-slate-400 hover:text-cyan-400 transition-colors duration-300">
                  AI Consulting
                </a>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-white font-bold mb-4">Contact Us</h3>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <Mail className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                <a
                  href={`mailto:${companyInfo.email}`}
                  className="text-slate-400 hover:text-cyan-400 transition-colors duration-300"
                >
                  {companyInfo.email}
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <Phone className="w-5 h-5 text-teal-400 flex-shrink-0 mt-0.5" />
                <a
                  href={`tel:${companyInfo.phone}`}
                  className="text-slate-400 hover:text-teal-400 transition-colors duration-300"
                >
                  {companyInfo.phone}
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                <span className="text-slate-400">{companyInfo.address}</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="py-6 border-t border-slate-800">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-slate-500 text-sm">
              © {currentYear} {companyInfo.name}. All rights reserved.
            </p>
            <div className="flex space-x-6">
              <a href="#" className="text-slate-500 hover:text-cyan-400 text-sm transition-colors duration-300">
                Privacy Policy
              </a>
              <a href="#" className="text-slate-500 hover:text-cyan-400 text-sm transition-colors duration-300">
                Terms of Service
              </a>
              <a href="#" className="text-slate-500 hover:text-cyan-400 text-sm transition-colors duration-300">
                Cookie Policy
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;